import React, { useState } from 'react';
import { SearchIcon, SettingsIcon, DownloadIcon, FilterIcon, CalendarIcon, LayoutGridIcon, LayoutListIcon } from 'lucide-react';
import { OrderStatusBadge } from '../../components/admin/OrderStatusBadge';
import { OrderActions } from '../../components/admin/OrderActions';
import { CustomerContact } from '../../components/admin/CustomerContact';
import { StatusFilter } from '../../components/admin/StatusFilter';
export const EnhancedOrderManagement = () => {
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [selectedStatus, setSelectedStatus] = useState('all');
  // Sample orders data
  const orders = [{
    id: 'ORD-2023-28764',
    timestamp: '6 minutes ago',
    product: {
      name: 'iPhone 13 Pro Max',
      specs: '128GB, Graphite'
    },
    customer: {
      name: 'David Cooper',
      email: 'david.cooper@example.com',
      phone: '+1234567890'
    },
    orderDate: 'Nov 18, 2023',
    amount: 899.0,
    status: 'pending',
    returnReason: null
  }, {
    id: 'ORD-2023-28763',
    timestamp: '3 hours ago',
    product: {
      name: 'Samsung Galaxy S22',
      specs: '256GB, Phantom Black'
    },
    customer: {
      name: 'Sarah Johnson',
      email: 'sarah.j@example.com',
      phone: '+1234567891'
    },
    orderDate: 'Nov 18, 2023',
    amount: 749.0,
    status: 'pending-collection',
    returnReason: null
  }
  // ... more orders
  ];
  return <div className="space-y-6" data-id="element-1597">
      {/* Header Section */}
      <div className="flex justify-between items-start" data-id="element-1598">
        <div data-id="element-1599">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1600">Order Management</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-1601">
            Manage all orders including returns and refunds
          </p>
          <div className="mt-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full inline-flex items-center" data-id="element-1602">
            <span className="font-semibold mr-1" data-id="element-1603">28,764</span>
            <span className="text-sm" data-id="element-1604">total orders</span>
          </div>
        </div>
        <div className="flex items-center space-x-3" data-id="element-1605">
          <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center" data-id="element-1606">
            <DownloadIcon className="h-4 w-4 mr-2" data-id="element-1607" />
            Export Orders
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 rounded-md" data-id="element-1608">
            <SettingsIcon className="h-5 w-5" data-id="element-1609" />
          </button>
        </div>
      </div>
      {/* Filters Section */}
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200" data-id="element-1610">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between" data-id="element-1611">
          <div className="flex-1 w-full md:w-auto" data-id="element-1612">
            <div className="relative" data-id="element-1613">
              <input type="text" placeholder="Search orders..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1614" />
              <SearchIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" data-id="element-1615" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3" data-id="element-1616">
            <StatusFilter selected={selectedStatus} onChange={setSelectedStatus} data-id="element-1617" />
            <button className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 hover:bg-gray-50" data-id="element-1618">
              <CalendarIcon className="h-4 w-4 mr-2" data-id="element-1619" />
              Date Range
            </button>
            <div className="flex items-center border border-gray-300 rounded-md" data-id="element-1620">
              <button className={`p-2 ${viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setViewMode('list')} data-id="element-1621">
                <LayoutListIcon className="h-5 w-5" data-id="element-1622" />
              </button>
              <button className={`p-2 ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setViewMode('grid')} data-id="element-1623">
                <LayoutGridIcon className="h-5 w-5" data-id="element-1624" />
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-1625">
        <div className="overflow-x-auto" data-id="element-1626">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1627">
            <thead className="bg-gray-50" data-id="element-1628">
              <tr data-id="element-1629">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1630">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1631">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1632">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1633">
                  Order Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1634">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1635">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1636">
                  Return Reason
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1637">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1638">
              {orders.map((order, index) => <tr key={order.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors`} data-id="element-1639">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1640">
                    <div className="text-sm font-medium text-gray-900" data-id="element-1641">
                      {order.id}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-1642">
                      {order.timestamp}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-1643">
                    <div className="text-sm text-gray-900" data-id="element-1644">
                      {order.product.name}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-1645">
                      {order.product.specs}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-1646">
                    <CustomerContact customer={order.customer} data-id="element-1647" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1648">
                    {order.orderDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1649">
                    <div className="text-sm font-medium text-gray-900" data-id="element-1650">
                      ${order.amount.toFixed(2)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1651">
                    <OrderStatusBadge status={order.status} data-id="element-1652" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1653">
                    {order.returnReason || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1654">
                    <OrderActions status={order.status} data-id="element-1655" />
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};